/**
 * 운영자 고객 상세의 「GEO 작업」 — 회차별 건수 → 회차 산출물 → 진행 중 작업 (261007-admin-brand-works).
 *
 * ★ **운영자 전용이다.** `BrandDetailPage` 는 `/app` 에도 걸린 공용 화면이라, 부르는 쪽이 `isAdmin` 일 때만
 *   이 컴포넌트를 마운트한다. 여기서 부르는 라우트는 전부 `access: 'admin'` 이다 — 고객 역할로는 403 이다.
 * ★ 산출물 카드는 고객 리포트의 `Deliverables` 를 그대로 쓴다 — 운영자가 **고객이 보는 것과 같은 화면**을 본다.
 *   단 영상은 **확정분만** 넘긴다(고객 응답과 같은 조건). 예약은 아래 「진행 중 작업」이 따로 보인다.
 * ★ 링크는 `/admin/…` 리터럴을 쓴다 — 이 컴포넌트는 운영자 영역에서만 그려진다.
 */
import type { ArticleSummary } from '@geo-admin/shared/contract/api/article.ts';
import { article, works } from '@geo-admin/shared/contract/api/index.ts';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import type { z } from 'zod';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';
import { PLATFORM_LABEL, useCycleBack } from '../lib/works.ts';
import { ArticleStatusBadge } from './ArticleParts.tsx';
import { CyclePicker } from './CyclePicker.tsx';
import { DataTable } from './data-table.tsx';
import { Deliverables } from './Deliverables.tsx';
import { Button } from './ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from './ui/card.tsx';
import { ErrorBox, Loading } from './ui/states.tsx';

type AdminWorks = z.infer<typeof works.getAdminWorks.response>;
type SummaryRow = z.infer<typeof works.getAdminWorksSummary.response>['rows'][number];
type Pending = AdminWorks['pending'][number];
type ScheduleStatus = Pending['status'];

const SUMMARY_CYCLES = 6;

const SCHEDULE_STATUS: Record<ScheduleStatus, { label: string; tone: 'slate' | 'green' | 'amber' | 'red' | 'blue' }> = {
  scheduled: { label: '예약됨', tone: 'blue' },
  sending: { label: '게시 중', tone: 'blue' },
  // Buffer 는 게시했다는데 우리 게시 행이 없다 — 다음 확인에서 채워지거나 주소가 비어 있는 것이다
  sent: { label: '게시됨 · 미확정', tone: 'amber' },
  error: { label: '실패', tone: 'red' },
  missing: { label: 'Buffer 에 없음', tone: 'red' },
  mismatch: { label: '플랫폼 불일치', tone: 'red' },
};

const n = (v: number) => (v === 0 ? <span className="text-slate-300">0</span> : <span>{v}</span>);

export function BrandWorksAdmin({ brandId }: { brandId: string }) {
  // 처음 여는 회차는 서버가 정한다 — 고객 보고서와 같다(261020-report-cycle-yesterday-basis)
  const { back, param, setBack, learn } = useCycleBack(brandId);
  const summary = useApi(works.getAdminWorksSummary, { query: { brandId, cycles: SUMMARY_CYCLES } });
  const data = useApi(works.getAdminWorks, { query: { brandId, cycle: param } });
  useEffect(() => learn(data.data?.openBack), [data.data?.openBack, learn]);

  return (
    <>
      <SummaryCard q={summary} back={back} onBack={setBack} />
      <Card>
        <CardHeader
          title="회차 산출물"
          description="고객 리포트와 같은 회차·같은 카드입니다"
          actions={<CyclePicker cycle={data.data?.cycle} back={back} onBack={setBack} />}
        />
        <CardBody>
          {data.error ? (
            <ErrorBox error={data.error} />
          ) : !data.data ? (
            <Loading />
          ) : (
            <div className={data.isFetching ? 'opacity-40 transition-opacity' : 'transition-opacity'}>
              <Deliverables data={{ ...data.data, videos: data.data.videos.filter((v) => v.posts.length > 0) }} />
              <InternalDetail works={data.data} />
            </div>
          )}
        </CardBody>
      </Card>
      <PendingCard pending={data.data?.pending} loading={!data.data && !data.error} />
      <ArticlesCard brandId={brandId} />
    </>
  );
}

function SummaryCard({
  q,
  back,
  onBack,
}: {
  q: ReturnType<typeof useApi<typeof works.getAdminWorksSummary>>;
  back: number;
  onBack: (b: number) => void;
}) {
  const rows = (q.data?.rows ?? []).map((r, i) => ({ ...r, back: i }));
  const columns: ColumnDef<SummaryRow & { back: number }, unknown>[] = [
    {
      header: '회차',
      id: 'cycle',
      cell: ({ row }) => (
        <span className={row.original.back === back ? 'font-semibold text-slate-900' : undefined}>
          {row.original.cycle.label}
          <span className="ml-2 text-slate-400 text-xs">
            {row.original.cycle.from.slice(5)} ~ {row.original.cycle.to.slice(5)}
          </span>
          {row.original.back === back ? <Badge className="ml-2">보는 중</Badge> : null}
        </span>
      ),
    },
    { header: '글', accessorKey: 'posts', cell: ({ row }) => n(row.original.posts) },
    { header: '기사', accessorKey: 'press', cell: ({ row }) => n(row.original.press) },
    {
      header: '영상 (게시)',
      accessorKey: 'videos',
      cell: ({ row }) => (
        <span>
          {n(row.original.videos)}
          {row.original.videoPosts > 0 ? (
            <span className="ml-1 text-slate-400 text-xs">({row.original.videoPosts})</span>
          ) : null}
        </span>
      ),
    },
    { header: '블로그', accessorKey: 'reviews', cell: ({ row }) => n(row.original.reviews) },
    { header: '순위 확인', accessorKey: 'rankings', cell: ({ row }) => n(row.original.rankings) },
    {
      header: '예약 대기',
      accessorKey: 'schedulesPending',
      cell: ({ row }) =>
        row.original.schedulesPending > 0 ? <Badge tone="amber">{row.original.schedulesPending}</Badge> : n(0),
    },
  ];
  return (
    <Card>
      <CardHeader
        title="회차별 산출물"
        description={`최근 ${SUMMARY_CYCLES}회차 · 그 회차에 발행·확인된 건수 · 행을 누르면 아래가 그 회차로 바뀝니다`}
      />
      <CardBody className="p-0">
        {q.error ? (
          <ErrorBox error={q.error} />
        ) : q.isLoading ? (
          <Loading />
        ) : (
          <DataTable columns={columns} data={rows} empty="회차가 없습니다" onRowClick={(r) => onBack(r.back)} />
        )}
      </CardBody>
    </Card>
  );
}

/** 내부 이름 — slug·path·연결 질문·externalKey. 평소에는 접어 둔다(카드가 본문이다) */
function InternalDetail({ works: d }: { works: AdminWorks }) {
  const [open, setOpen] = useState(false);
  const posts = d.homepages.flatMap((h) => h.posts.map((p) => ({ ...p, slug: h.slug })));
  const keyed = [
    // 언론 보도는 없다 — 기사 게재가 출처라 외부 키가 없다(261010-press-article-only-og). 원고는 아래 기사 카드가 본다
    ...d.videos.map((x) => ({
      kind: x.posts.length > 0 ? '영상' : '영상 · 미확정',
      title: x.title,
      date: x.publishedOn,
      key: x.externalKey,
      url: x.sourceUrl,
    })),
    ...d.reviews.map((x) => ({ kind: '블로그', title: x.title, date: x.publishedOn, key: x.externalKey, url: x.url })),
  ];
  return (
    <div className="mt-6 border-slate-200 border-t pt-4">
      <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>
        {open
          ? '내부 상세 접기'
          : `내부 상세 보기 · 홈페이지 ${d.homepages.length} · 글 ${posts.length} · 그 밖 ${keyed.length}`}
      </Button>
      {open ? (
        <div className="mt-4 space-y-6 text-sm">
          <div>
            <h3 className="mb-2 font-semibold text-slate-700">홈페이지 · 글</h3>
            {d.homepages.length === 0 ? (
              <p className="text-slate-400">등록된 홈페이지가 없습니다</p>
            ) : (
              <ul className="mb-2 text-slate-600">
                {d.homepages.map((h) => (
                  <li key={h.slug}>
                    <code className="text-xs">{h.slug}</code> · {h.url} · 이 회차 {h.postCount}편
                  </li>
                ))}
              </ul>
            )}
            {posts.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-slate-200 border-b text-left text-slate-500 text-xs">
                    <th className="py-1 pr-3">발행</th>
                    <th className="py-1 pr-3">사이트 · 경로</th>
                    <th className="py-1 pr-3">제목</th>
                    <th className="py-1">연결 질문</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.url} className="border-slate-100 border-b align-top">
                      <td className="whitespace-nowrap py-1 pr-3 tabular-nums">{p.publishedOn}</td>
                      <td className="py-1 pr-3">
                        <code className="text-xs">
                          {p.slug}
                          {p.path}
                        </code>
                      </td>
                      <td className="py-1 pr-3">
                        <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">
                          {p.title}
                        </a>
                      </td>
                      <td className="py-1">
                        {p.queryId ? (
                          <Link to="/admin/queries/$id" params={{ id: p.queryId }} className="hover:underline">
                            {p.query ?? p.queryId}
                          </Link>
                        ) : (
                          <span className="text-slate-400">없음</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
          <div>
            <h3 className="mb-2 font-semibold text-slate-700">기사 · 영상 · 블로그 (externalKey)</h3>
            {keyed.length === 0 ? (
              <p className="text-slate-400">이 회차에 없습니다</p>
            ) : (
              <table className="w-full">
                <tbody>
                  {keyed.map((x) => (
                    <tr key={`${x.kind}:${x.key}`} className="border-slate-100 border-b align-top">
                      <td className="whitespace-nowrap py-1 pr-3 text-slate-500">{x.kind}</td>
                      <td className="whitespace-nowrap py-1 pr-3 tabular-nums">{x.date}</td>
                      <td className="py-1 pr-3">
                        {x.url ? (
                          <a href={x.url} target="_blank" rel="noreferrer" className="hover:underline">
                            {x.title}
                          </a>
                        ) : (
                          x.title
                        )}
                      </td>
                      <td className="break-all py-1">
                        <code className="text-slate-500 text-xs">{x.key}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PendingCard({ pending, loading }: { pending: Pending[] | undefined; loading: boolean }) {
  const columns: ColumnDef<Pending, unknown>[] = [
    { header: '예정', accessorKey: 'dueAt', cell: ({ row }) => dateTime(row.original.dueAt) },
    {
      header: '영상',
      accessorKey: 'videoTitle',
      cell: ({ row }) => (
        <span title={row.original.externalKey}>
          {row.original.videoTitle}
          <span className="ml-2 text-slate-400 text-xs">발행일 {row.original.publishedOn}</span>
        </span>
      ),
    },
    { header: '플랫폼', accessorKey: 'platform', cell: ({ row }) => PLATFORM_LABEL[row.original.platform] },
    {
      header: '상태',
      accessorKey: 'status',
      cell: ({ row }) => {
        const s = SCHEDULE_STATUS[row.original.status];
        return (
          <span className="flex flex-wrap items-center gap-1">
            <Badge tone={s.tone}>{s.label}</Badge>
            {row.original.status === 'mismatch' && row.original.channelService ? (
              <span className="text-slate-500 text-xs">Buffer: {row.original.channelService}</span>
            ) : null}
            {row.original.sentAt ? (
              <span className="text-slate-500 text-xs">게시 {dateTime(row.original.sentAt)}</span>
            ) : null}
          </span>
        );
      },
    },
  ];
  return (
    <Card>
      <CardHeader
        title="진행 중 — 영상 예약"
        description="Buffer 로 예약했지만 아직 게시 주소가 확정되지 않은 것 · 고객에게는 보이지 않습니다"
        actions={<BufferSyncBar />}
      />
      <CardBody className="p-0">
        {loading ? (
          <Loading />
        ) : (
          <DataTable columns={columns} data={pending ?? []} empty="확정을 기다리는 예약이 없습니다" />
        )}
      </CardBody>
    </Card>
  );
}

/** Buffer 확인 — 하루 한 번(4시) 저절로 돈다. `articles.tsx` 의 `PressSyncBar` 와 같은 모양 */
function BufferSyncBar() {
  const got = useApi(works.getBufferSync);
  const run = useApiMutation(works.runBufferSync);
  const [error, setError] = useState<string | null>(null);
  const last = run.data?.last ?? got.data?.last ?? null;

  const onRun = async () => {
    setError(null);
    try {
      await run.mutateAsync({});
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 text-slate-500 text-xs">
      {error ? (
        <span className="text-bad">{error}</span>
      ) : last ? (
        <span title={last.error ?? undefined}>
          마지막 확인 {dateTime(last.at)}
          {last.ok ? ` · 확정 ${last.confirmed ?? 0}건` : ' · 실패'}
          {last.manual ? '' : ' (자동)'}
        </span>
      ) : got.data ? (
        <span>{got.data.configured ? '매일 4시에 확인합니다' : 'Buffer 키가 설정되지 않았습니다'}</span>
      ) : null}
      <Button
        size="sm"
        variant="outline"
        onClick={() => void onRun()}
        disabled={run.isPending || got.data?.configured === false}
      >
        {run.isPending ? '확인 중…' : '지금 확인'}
      </Button>
    </div>
  );
}

/**
 * 이 고객의 기사 원고 — 전역 `/admin/articles` 목록을 고객으로 거른다(서버 변경 없음, 건수가 작다).
 * ★ 고객 리포트의 언론 보도는 이 원고들의 **게재**(`article_publication`)다(261010-press-article-only-og) —
 *   여기는 원고의 진행 단계, 리포트는 게재된 결과를 본다.
 */
function ArticlesCard({ brandId }: { brandId: string }) {
  const list = useApi(article.listArticles);
  const mine = (list.data?.articles ?? []).filter((a) => a.brandId === brandId);
  const count = (s: ArticleSummary['status']) => mine.filter((a) => a.status === s).length;
  const columns: ColumnDef<ArticleSummary, unknown>[] = [
    {
      header: '제목',
      accessorKey: 'title',
      cell: ({ row }) => (
        <Link to="/admin/articles/$id" params={{ id: row.original.id }} className="font-medium hover:underline">
          {row.original.title}
        </Link>
      ),
    },
    { header: '질문', accessorKey: 'query' },
    {
      header: '상태',
      accessorKey: 'status',
      cell: ({ row }) => (
        <span className="flex flex-wrap gap-1">
          <ArticleStatusBadge status={row.original.status} />
          {row.original.publicationCount > 0 ? (
            <Badge tone="blue">언론사 {row.original.publicationCount}곳</Badge>
          ) : null}
        </span>
      ),
    },
    {
      header: '마지막 단계',
      id: 'stage',
      cell: ({ row }) => {
        const a = row.original;
        const [label, at] = a.publishedAt
          ? ['게재', a.publishedAt]
          : a.approvedAt
            ? ['승인', a.approvedAt]
            : a.sentAt
              ? ['검토 요청', a.sentAt]
              : ['작성', a.createdAt];
        return (
          <span className="text-slate-600">
            {label} {dateTime(at)}
          </span>
        );
      },
    },
  ];
  return (
    <Card>
      <CardHeader
        title="진행 중 — 기사 원고"
        description={
          list.data
            ? `작성 중 ${count('draft')} · 검토 중 ${count('sent')} · 승인 ${count('approved')} · 게재 ${count('published')}`
            : undefined
        }
      />
      <CardBody className="p-0">
        {list.error ? (
          <ErrorBox error={list.error} />
        ) : list.isLoading ? (
          <Loading />
        ) : (
          <DataTable columns={columns} data={mine} empty="이 고객의 기사 원고가 없습니다" />
        )}
      </CardBody>
    </Card>
  );
}
